import { useSeo } from "@/lib/seo";
import { Link } from "react-router-dom";
import { Target, Eye, HandHeart, Mail, Award, Briefcase, GraduationCap, HeartHandshake } from "lucide-react";

const WOLF = "https://images.unsplash.com/photo-1664573927713-74677f202d42?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

const Founder = ({ name, role, credentials, photo, photoPos = "center", children }) => (
  <div className="bg-white border border-[#0A192F]/10 p-8" data-testid={`founder-${name.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className={photo ? "flex flex-col md:flex-row gap-8" : ""}>
      {photo && (
        <div className="md:w-64 md:shrink-0">
          <div className="relative overflow-hidden border-2 border-[#D4AF37]/50">
            <img src={photo} alt={name} style={{ objectPosition: photoPos }} className="w-full aspect-[3/4] object-cover" data-testid={`founder-photo-${name.replace(/\s+/g, "-").toLowerCase()}`} />
          </div>
        </div>
      )}
      <div className="flex-1">
        <p className="overline text-[#8B6D1F]">{role}</p>
        <h3 className="font-display text-3xl font-black tracking-tight text-[#0A192F] mt-1">{name}</h3>
        {credentials?.length > 0 && (
          <ul className="mt-4 flex flex-wrap gap-2">
            {credentials.map((c) => (
              <li key={c} className="text-xs px-3 py-1.5 border border-[#0A192F]/20 rounded-full text-[#0A192F] bg-[#0A192F]/5">
                {c}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-5 space-y-4 text-[#1a2b45] leading-relaxed">{children}</div>
      </div>
    </div>
  </div>
);

const SubBlock = ({ Icon, title, children }) => (
  <div className="mt-5 border-t border-[#0A192F]/10 pt-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#D4AF37]" />
      <h4 className="overline text-[#0A192F]">{title}</h4>
    </div>
    <div className="text-[#4A5568] text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function About() {
  useSeo({ title: "About Us", description: "About Wolf-dog News — our all-volunteer newsroom, founders, and mission." });

  return (
    <div>
      <div className="relative h-72 bg-[#0A192F]">
        <img src={WOLF} alt="Wolfdog" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <p className="overline text-[#D4AF37]">Our Story</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mt-1">About Wolf-dog News</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-14">
        {/* Org intro */}
        <p className="text-xl text-[#1a2b45] leading-relaxed">
          Wolf-dog News is an all volunteer organization, with no owner, no investors, no money exchanging hands to
          control, compel or otherwise interfere with the integrity of our reporting. All of our writers are well
          experienced wolf dog professionals sharing with you the most precise and trustworthy factual content that
          comes from decades of intensive research, investigation, education and real world experience.
        </p>
        <p className="mt-5 text-lg text-[#4A5568] leading-relaxed">
          Our strength comes from our diversity of experience with wolf-dogs and wolf-dog issues; we have writers from
          wolf-dog sanctuaries, wolf-dog breeders, wolf-dog rescuers, wolf-dog behaviorists and cognitive specialists.
          In addition to fact based reporting, we will also publish editorials, interviews, containment examples,
          content information and dispelling prevarication. We are committed to delivering the most comprehensive and
          current information on all wolf-dog topics.
        </p>

        {/* Values */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
          {[
            { Icon: Target, title: "Truth", body: "Accurate, well-sourced reporting free from sensationalism." },
            { Icon: Eye, title: "Awareness", body: "Making legislation and current events understandable to everyone." },
            { Icon: HandHeart, title: "Protection", body: "Advocating for responsible ownership and animal welfare." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="bg-white border border-[#0A192F]/10 p-6">
              <Icon className="w-7 h-7 text-[#D4AF37]" />
              <h3 className="font-display text-lg font-bold text-[#0A192F] mt-3">{title}</h3>
              <p className="mt-2 text-sm text-[#4A5568] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* Founders */}
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0A192F] mt-16 mb-2">
          Wolf Dog News Founders
        </h2>
        <div className="h-1 w-20 bg-[#D4AF37] mb-8" />

        <div className="space-y-8">
          <Founder
            name="Lily Madaffari"
            role="Founder & Editor-In-Chief"
            photo="https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-central/artifacts/jmr6526k_IMG_3194.jpeg"
            credentials={[
              "Wolfdog Breeder — 18 years",
              "Wolfdog Rescue — 13 years",
              "Low Content Wolfdogs & Developmental Breeds",
              "U.S. Marine Corps Veteran",
              "Former Crime Scene Investigator",
            ]}
          >
            <p>
              My wolfdog journey began with a little bit of peer pressure. I was an Alaskan Malamute breeder at the time,
              a friend that I served in the Marine Corps with kept asking me to get involved with wolfdogs. I had seen
              photos of his wolfdogs and, despite their breathtaking beauty, I had reservations about going near them. My
              first reaction upon meeting them in person for the first time was fear. Of course, I wasn’t going to tell a
              fellow Marine that I was afraid (but, secretly, I was.)
            </p>
            <p>
              I entered the enclosure and sat on a block wall. Instantly, the wolfdogs surrounded me. My heart was
              racing, I thought, for sure, today was going to be the end of me. Then it happened, the first wolfdog kiss.
              Not a big, slobbery dog kiss, but a gentle feather kiss on my cheek that told me I was safe with them. That
              was the moment it all began. The next moment I had a 100 pound wolfdog climbing into my lap to be held like
              a baby. I was astonished by their gentle nature.
            </p>
          </Founder>

          <Founder
            name="Tony Nila"
            role="Co-Founder & Managing Editor · Expert Contributor"
            photo="https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-central/artifacts/uiesclnh_IMG_3196.png"
            photoPos="22% center"
            credentials={["Canine Cognitive Specialist", "Founder, Tony-Nila.com", "Author, Raising Your Perfect Puppy"]}
          >
            <p>
              Tony Nila is a respected Canine Cognitive Specialist with over a decade of experience helping dogs and
              their owners build trust, boundaries, and harmony. As the founder of Tony-Nila.com, Tony’s groundbreaking
              approach focuses on Cognitive learning, trust-based communication, and respecting a dog’s natural
              instincts.
            </p>
            <p>Unlike traditional obedience training methods, Tony’s philosophy emphasizes:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Boundaries over obedience for emotional and cognitive growth.</li>
              <li>No tools, no treats, no force, just natural learning and mutual understanding.</li>
            </ul>
            <p>
              His unique methods have empowered countless dog owners to transform their relationships and raise
              well-balanced, independent dogs.
            </p>
            <p>
              Drawing inspiration from universal truths about behavior, Tony Nila rejects outdated training ideas and
              challenges the status quo. His work bridges the gap between dogs’ wild instincts and domestic needs,
              creating a partnership rooted in mutual respect. Tony’s methods have been inspired by leading canine
              experts and scientists, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Charles P. Eisenmann and his work with the famed dog London.</li>
              <li>Edward Tolman (U.C Berkeley) and his groundbreaking insights into latent learning.</li>
            </ul>
            <p>
              Tony’s mission is simple yet profound: to help dog owners build deeper connections with their canine
              companions by understanding and respecting their cognitive and emotional needs. Through his book, Raising
              Your Perfect Puppy, and personalized coaching services, Tony inspires others to rethink dog training and
              embrace natural, trust-based methods.
            </p>
            <p>
              When Tony isn’t helping clients, he is a passionate educator, speaker, and advocate for animal welfare. His
              innovative work has transformed the lives of dog owners worldwide, redefining what it means to live in
              harmony with man’s best friend.
            </p>

            <SubBlock Icon={Award} title="Skills">
              <ul className="list-disc pl-6 space-y-1">
                <li>Canine Cognitive Learning &amp; Behavior Modification</li>
                <li>Virtual Training &amp; Remote Behavioral Consultations</li>
                <li>Adoption Counseling &amp; Pet Retention Strategies</li>
                <li>Social Media Strategy for Animal Welfare</li>
                <li>Public Speaking &amp; Educational Workshops</li>
                <li>Shelter Enrichment &amp; Behavioral Assessments</li>
              </ul>
            </SubBlock>

            <SubBlock Icon={Briefcase} title="Experience">
              <p className="font-semibold text-[#0A192F]">May 2024 – Current · Canine Behavior &amp; Adoption Specialist, Joyful Pets Animal Rescue</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Conduct virtual behavioral assessments for dogs, evaluating temperament, socialization skills, and training needs.</li>
                <li>Develop customized training and rehoming strategies, ensuring well-matched placements and reducing adoption returns.</li>
                <li>Provide one-on-one behavioral coaching via Zoom to adopters, guiding them through positive reinforcement techniques.</li>
                <li>Collaborate with shelters and rescue organizations to improve adoption outcomes and support long-term pet retention.</li>
              </ul>
              <p className="font-semibold text-[#0A192F] mt-3">January 1995 – Current · Founder &amp; Canine Behavior Specialist, Tony-Nila.com | Rutherfordton, USA</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Pioneered cognitive learning techniques for canine behavior modification, successfully addressing aggression, anxiety, and obedience issues.</li>
                <li>Designed and led training workshops and educational seminars for pet owners and industry professionals.</li>
                <li>Worked under Dr. Darwin Cole, PhD in Comparative Psychology, developing behavior modification programs for high-content wolfdogs.</li>
                <li>Authored educational materials and blog articles on canine cognition and humane training methods.</li>
              </ul>
            </SubBlock>

            <SubBlock Icon={GraduationCap} title="Education and Training">
              <p className="font-semibold text-[#0A192F]">January 2024 · Certificate in Dog Emotion &amp; Cognition, Duke University</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Studied dog psychology, cognitive processing, and emotional intelligence in canines.</li>
                <li>Applied scientific research to real-world training and behavior intervention methods.</li>
              </ul>
              <p className="font-semibold text-[#0A192F] mt-3">Canine Behavior Specialist Program</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Internship under Dr. Cole, PhD in Comparative Psychology | 8-Year Mentorship (Assisted in developing and implementing behavioral modification programs for high-content wolfdogs.)</li>
                <li>Specialized in canine cognition, behavior assessment, and environmental enrichment strategies.</li>
              </ul>
            </SubBlock>

            <SubBlock Icon={HeartHandshake} title="Volunteer Work and Special Projects">
              <ul className="list-disc pl-6 space-y-1">
                <li>Animal Behavior Writer: Regular contributor to blogs on humane training methods and canine psychology.</li>
                <li>Rescue &amp; Rehabilitation: Volunteer for local and remote animal rescue missions, providing behavioral assessments and training support.</li>
              </ul>
            </SubBlock>
          </Founder>

          <Founder
            name="Lori Wynn"
            role="Co-Founder & Managing Editor · Contributing Writer"
            photo="https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-central/artifacts/ha6jh0s3_IMG_3195.jpeg"
            photoPos="78% center"
            credentials={[
              "Wolf Dog Owner — 26 years",
              "Wolf Dog Rescue — 15 years",
              "Wolf/Wolf Dog Educator & Legislative Advocate — 12 years",
              "General & Plumbing Contractor",
              "Founder of Guardians of the Wolves",
            ]}
          >
            <p>
              My wolf dog journey began when a dear native American man gifted me a pure wolf as I was told; not knowing
              anything about wolves or wolf dogs, I believed that I had a wolf. This animal bonded with, at the time, my
              nine-year-old daughter and not with me. Not knowing anything about this breed as I had only experience with
              German shepherds and pit bulls. Desiring to learn more about wolf dogs I literally searched the world for a
              responsible breeder. I was referred to an original wolf dog breeder from many sources throughout the world
              who resided in Texas. I sent her photos of my “wolf” and learned from her that my “wolf” who was born in
              November with one blue eye was actually a low to no content wolf dog.
            </p>
            <p>
              I asked this woman a lot of questions and she was very patient on educating me. I expressed to her my
              desire to live with one of these animals. She sent me photos of the puppies of that year along with
              handwritten family trees and what grabbed my attention the most was a liter born the day and year of the
              passing of my best friend. I asked her if I could please have one of the black puppies and if she would
              mentor me as I knew nothing. Gratefully she agreed. My puppy arrived at the airport. Once I got him to my
              car, I put my hand in his little crate to hold him and clean him, and he was like a little vicious bear.
              This was the first time that I met a wolf dog in person. I initially was frightened by his sharp teeth and
              nails. Once I brought him home, I instinctively began hand feeding him, which was not an easy task with
              those razor-sharp puppy teeth. Again, not knowing anything nor having the actual resources, I instinctively
              used my experience of raising pit bulls for 20 years of my adult life. I later learned through my mentor
              that this animal was a true “Davidson Fur Farm” ancestor.
            </p>
            <p>
              I just wanted to learn more and more, and I am so grateful for the woman I met to become my mentor. Once he
              turned two, I called her and stated that he needed a companion. She said, “I was waiting for you to call.”
              And there it was…I acquired my 2nd wolf dog, and two years later a third. Immediately after embarking on
              wolf dog ownership, I knew this was the lifestyle for me. Different from most other lifestyles. It was the
              spirit, the energy, the love, the kindness that I never experienced with another animal that made me feel
              at home. From there I volunteered at a sanctuary and became a board member and then went to another
              sanctuary where my 2nd wolf dog, Sage, became an ambassador for them. We went out in public for this
              sanctuary and performed educational presentations regarding wolves and wolf dogs. The founder of this
              sanctuary, a native American woman who lived with these animals since she was 4 years old became another
              mentor for me. I personally don’t recommend on taking the road that I did as finding two amazing women to
              mentor me was divinity that most people will never get.
            </p>
            <p>
              So here we are today, and I want to give back to people who were like me who knew nothing about wolf dogs
              what I was so grateful to receive from my two mentors along with my 26 years of hands-on experience.
            </p>
          </Founder>

          <Founder
            name="Melissa Gatto"
            role="Contributing Writer"
            photo="https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-central/artifacts/y23cm1mz_IMG_3197.jpeg"
            photoPos="15% center"
            credentials={[
              "Wolfdog Owner — 8 years",
              "Wolfdog Rescue/Rehab — 7 years",
              "Wolfdog Breeder — 4 years",
              "Low & Mid-Content Wolfdogs",
              "Biological, Animal Behavioral & Ecological Sciences",
              "Grooming Bather",
            ]}
          >
            <p>
              8 years ago, I made a Facebook post sharing a viral photo of a giant black wolfdog inside a pet store on a
              leash. A friend of mine commented under my post saying her neighbor just had litter of 75% wolfdog puppies
              the other day and he still had 3 puppies available. Out of impulse and sheer excitement, I went forward
              with a deposit on a puppy.
            </p>
            <p>
              A few months went by, and I found a Facebook wolfdog group and shared pictures of my puppy. Immediately, I
              was caught off guard when I checked my flood of notifications of aggressive comments with a similar theme,
              “that’s not a wolfdog!”
            </p>
            <p>
              In my mind, there was no way a trusted friend would have led me astray on my purchase, after all, I had
              seen the animals in person, so I went on to buy my second wolfdog from the same breeder, but this one was a
              female and “90% wolf.” Four months after bringing my girl home, the breeder had her brother returned. Since
              I had originally wanted this puppy but someone else had a deposit on him, he called me and asked if I’d like
              to rehabilitate him because he was in rough shape.
            </p>
            <p>
              Two years into wolfdog ownership I purchased my Embark DNA tests, thinking I’ll finally prove I was right,
              and that those people that were always coming after me in the groups were wrong. The results of those tests
              showed that my first wolfdog was 7% Gray Wolf, my second 32%, and my third was 29%.
            </p>
            <p>
              My wolfdog beginnings are far from uncommon. Over the last 8 years, I’ve encountered a countless number of
              people who have had near identical experiences, making my goal to help gently lead those people towards
              figuring out they too have been duped, because I wasn’t treated as delicately. I had to learn everything the
              hard way and was fearful of asking for help, and if I can help someone just as stubborn as I was, and gain
              another ally in wolfdog truth, then I’m not just helping people, but I’m saving the lives of wolfdogs and
              wolfy-looking dogs from the comfort of my own home.
            </p>
            <p>
              The last 6 years I have dedicated to wolfdog education, rescue, rehab, and am now assisting on producing
              low-content wolfdogs for an in development American wolfdog breed. Moving forward I hope to boldly bust
              wolfdog myths and aid in moving internet algorithms in the correct direction with unbiased facts, as well
              as cover stories dispelling the myth of the big bad wolfdog.
            </p>
          </Founder>

          <Founder
            name="Crystal DeSantiago"
            role="Contributing Writer"
            photo="https://customer-assets-7cd3h4nn.emergentagent.net/job_wolf-central/artifacts/gzo4vspd_att.LZXFOOubyrxc0azS7_33rVbtTFC4QVXL4laFoQsHAyw.jpeg"
            photoPos="center top"
            credentials={[
              "Animal Control Officer",
              "Certified Cruelty Investigator",
              "Canine Behaviorist — 32 years",
              "Certified Veterinary Technician — 25 years",
              "High-Drive & Primitive Canine Specialist",
              "Wolf & Wolfdog Advocate",
            ]}
          >
            <p>
              Crystal DeSantiago is an animal welfare specialist, Animal Control Officer, certified cruelty
              investigator, and canine behaviorist with over 32 years of frontline experience in humane law enforcement,
              field rescue operations and private security. She also has 25 years of veterinary medicine as a certified
              veterinary technician. Bridging public safety with compassionate animal management, she specializes in
              high-drive animals, advanced behavioral modification, and the management of complex, high-drive working
              breeds and primitive canines.
            </p>
            <p>
              Throughout her career, DeSantiago has been a dedicated advocate for wolves and wolfdogs, championing
              ethical intervention, specialized containment standards, and the rehabilitation of misunderstood animals.
              Her work integrates deep investigative expertise with behavioral science to resolve severe behavioral
              challenges, protect vulnerable domestic and wild-content canines, and educate communities on responsible
              animal stewardship. She is also working on becoming an Animal Cruelty Law Attorney and she lives with 4
              wolfdogs, a retired K9 Belgian Malinois and several rescues in the state of Florida. DeSantiago strives to
              educate all to keep communities safe and protect the voiceless animals we live alongside of today.
            </p>
          </Founder>
        </div>

        {/* Mission */}
        <div className="mt-16 bg-[#0A192F] text-white p-10 border-2 border-[#D4AF37]/50">
          <h2 className="font-display text-3xl font-black tracking-tight text-[#D4AF37]">Mission Statement</h2>
          <p className="mt-4 text-lg leading-relaxed text-white/90">
            At Wolf-dog News, our goal is to provide the local, national and global community with accurate, honest and
            reliable reporting on all issues pertaining to wolf-dogs and wolf hybrids, fostering a well informed
            citizenry, combating the abundance of online misinformation and speculation about wolf-dogs, exposing truths,
            myths, scientific corruption and bringing credible science, observation, experience and information to the
            forefront of wolf-dog reporting. We are dedicated to keeping our readers up to date with wolf-dog
            legislation, rabies vaccine status, behavior, genetics, containment and so much more.
          </p>
        </div>

        {/* Submit prompt */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white border border-[#0A192F]/10 p-6">
          <Mail className="w-7 h-7 text-[#D4AF37] shrink-0" />
          <p className="text-[#1a2b45] leading-relaxed">
            Is there a topic you would like us to write about? Would you like to submit a photo of your wolfdog for use
            on the website?{" "}
            <Link to="/contact" className="text-[#8B6D1F] font-semibold hover:underline">Please email us</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
